// Auth screen — App Flow §1, Content §4.1 copy, API §6 errors. Google first, email code as the fallback.
import { Button, Card, Input, Wordmark } from '@roots/ui';
import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import {
  type AuthClientError,
  authClient,
  markOAuthStarted,
  storeSessionToken,
  useAuth,
} from '../lib/auth.js';

type Step = 'choose' | 'code' | 'profile';

const RESEND_COOLDOWN_S = 30; // API §6

const COPY = {
  googleFailed: 'Google sign-in failed. Try your email instead.',
  badEmail: 'Couldn’t send a code to that email. Check the address and try again.',
  sendFailed: 'Couldn’t send code. Try again in a moment.',
  tooFast: 'You’re moving fast! Give it a moment.',
  wrongCode: 'That code didn’t match. Try again.',
  expired: 'Code expired. Send a new one to keep going.',
  lockedOut: 'Too many tries on that code. Send a new one to keep going.',
  profileFailed: 'We couldn’t save that. Check your connection and try again.',
};

function sendError(error: AuthClientError) {
  if (error.status === 429) return COPY.tooFast;
  if (error.status === 400) return COPY.badEmail;
  return COPY.sendFailed;
}

function verifyError(error: AuthClientError) {
  if (error.status === 429) return COPY.tooFast;
  if (error.code === 'OTP_EXPIRED') return COPY.expired;
  if (error.code === 'TOO_MANY_ATTEMPTS') return COPY.lockedOut;
  return COPY.wrongCode;
}

/** Where to land once signed in: a pending invite wins (App Flow §1). */
function destination() {
  const invite = sessionStorage.getItem('roots:invite');
  return invite ? `/join/${invite}` : '/';
}

export function AuthScreen({ initialStep }: { initialStep?: Step } = {}) {
  const { refresh, user, setUser } = useAuth();
  const [step, setStep] = useState<Step>(
    initialStep ?? (user && !user.name.trim() ? 'profile' : 'choose'),
  );
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [name, setName] = useState(user?.name ?? '');
  const [birthday, setBirthday] = useState(user?.birthday ?? '');
  const [error, setError] = useState<string | null>(() =>
    new URLSearchParams(window.location.search).has('error') ? COPY.googleFailed : null,
  );
  const [busy, setBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  // Drop ?error= from the address bar once shown, so a reload starts clean.
  useEffect(() => {
    if (window.location.search) window.history.replaceState(null, '', window.location.pathname);
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  // A Google sign-up lands here with the profile half-filled from Google.
  useEffect(() => {
    if (!user) return;
    setName((current) => current || user.name);
    setBirthday((current) => current || user.birthday || '');
  }, [user]);

  async function continueWithGoogle() {
    setGoogleBusy(true);
    setError(null);
    markOAuthStarted();
    const origin = window.location.origin;
    try {
      const res = await authClient.signIn.social({
        provider: 'google',
        callbackURL: `${origin}${destination()}`,
        newUserCallbackURL: `${origin}/welcome`,
        errorCallbackURL: `${origin}/auth`,
      });
      if (res.error) throw res.error;
      // The client follows the redirect to Google; keep the spinner until the page unloads.
    } catch {
      setError(COPY.googleFailed);
      setGoogleBusy(false);
    }
  }

  async function sendCode() {
    setBusy(true);
    setError(null);
    try {
      const res = await authClient.emailOtp.sendVerificationOtp({
        email: email.trim(),
        type: 'sign-in',
      });
      if (res.error) {
        setError(sendError(res.error));
        return;
      }
      setCode('');
      setStep('code');
      setCooldown(RESEND_COOLDOWN_S);
    } catch {
      setError(COPY.sendFailed);
    } finally {
      setBusy(false);
    }
  }

  async function verifyCode() {
    setBusy(true);
    setError(null);
    try {
      const res = await authClient.signIn.emailOtp({ email: email.trim(), otp: code });
      if (res.error) {
        setError(verifyError(res.error));
        if (res.error.code === 'OTP_EXPIRED' || res.error.code === 'TOO_MANY_ATTEMPTS') {
          setCode('');
          setCooldown(0);
        }
        return;
      }
      storeSessionToken(res.data?.token);
      const me = await refresh();
      if (!me) {
        setError(COPY.sendFailed);
        return;
      }
      if (me.name.trim()) window.location.replace(destination());
      else setStep('profile');
    } catch {
      setError(COPY.sendFailed);
    } finally {
      setBusy(false);
    }
  }

  async function saveProfile() {
    setBusy(true);
    setError(null);
    try {
      setUser(await api.updateMe({ name: name.trim(), birthday: birthday || null }));
      window.location.replace(destination());
    } catch {
      setError(COPY.profileFailed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-[440px] flex-col justify-center gap-6 p-6">
      <div className="text-center">
        <h1>
          <Wordmark className="mx-auto block h-14 w-auto text-[var(--color-text-primary)]" />
        </h1>
        <p className="type-caption mt-1">Your family, one magazine a month.</p>
      </div>

      {step === 'choose' && (
        <Card className="flex flex-col gap-4">
          <Button onClick={continueWithGoogle} loading={googleBusy} fullWidth>
            Continue with Google
          </Button>
          <p className="type-caption text-center">or use your email</p>
          <Input
            label="Email"
            type="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && email.includes('@') && !busy) void sendCode();
            }}
            placeholder="you@example.com"
            error={error ?? undefined}
          />
          <Button
            variant="secondary"
            onClick={sendCode}
            loading={busy}
            disabled={!email.includes('@')}
            fullWidth
          >
            Send me a code
          </Button>
        </Card>
      )}

      {step === 'code' && (
        <Card className="flex flex-col gap-4">
          <p className="type-body">We sent a 6-digit code to {email.trim()}.</p>
          <Input
            label="Code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && code.length === 6 && !busy) void verifyCode();
            }}
            error={error ?? undefined}
          />
          <Button onClick={verifyCode} loading={busy} disabled={code.length !== 6} fullWidth>
            Continue
          </Button>
          <Button variant="ghost" onClick={sendCode} disabled={busy || cooldown > 0} fullWidth>
            {cooldown > 0 ? `Send a new code (${cooldown}s)` : 'Send a new code'}
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              setStep('choose');
              setError(null);
            }}
            fullWidth
          >
            Use a different email
          </Button>
        </Card>
      )}

      {step === 'profile' && (
        <Card className="flex flex-col gap-4">
          <h2 className="type-heading">Nice to meet you</h2>
          <Input label="Your name" value={name} onChange={(e) => setName(e.target.value)} />
          <Input
            label="Birthday (optional)"
            type="date"
            value={birthday}
            onChange={(e) => setBirthday(e.target.value)}
            hint="Add your birthday so your family can celebrate you."
          />
          {error && <p className="type-caption text-[var(--color-error)]">{error}</p>}
          <Button
            onClick={saveProfile}
            loading={busy}
            disabled={name.trim().length === 0}
            fullWidth
          >
            Continue
          </Button>
        </Card>
      )}
    </main>
  );
}
